import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useEstablishmentCompaniesWithConsumption } from "@/common/hooks/queries/useEstablishmentCompaniesWithConsumption";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { Option } from "@/common/types/option";
import { billingEventsFiscalDocumentsDefaultColumns } from "@/features/billing/constants/eventsFiscalDocumentsDefaultColumns.constant";
import { billingEventsFiscalDocumentsStatusOptions } from "@/features/billing/constants/eventsFiscalDocumentsStatusOptions.constant";
import { useBillingEventsWithFiscalDocumentRequirement } from "@/features/billing/hooks/useBillingEventWithFiscalDocumentRequirement";
import { BillingEventsFiscalDocumentsTransformer } from "@/features/billing/services/eventsFiscalDocumentsTransformer";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { EstablishmentCompanyTransformer } from "@/features/companyBranches/services/establishmentCompanyTransformer";
import { CoinsIcon, Search } from "lucide-react";

const BillingEventsWithFiscalDocumentRequirementList = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const { date, formattedDate } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.number || '');
  const [searchClientBranch, setSearchClientBranch] = useDebounce('');
  const [searchEstablishmentCompany, setSearchEstablishmentCompany] = useDebounce('');

  const { data: clientBranchesOptions } = useAvailableCompanyBranches({
    filters: { search: searchClientBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { data: establishmentCompanyOptions } = useEstablishmentCompaniesWithConsumption({
    filters: { search: searchEstablishmentCompany },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  });

  const { data: billingEvents, meta, isFetching: billingEventsIsFetching } = useBillingEventsWithFiscalDocumentRequirement({
    filters: {
      page: filterParams.page || 1,
      search,
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      clientBranchId: filterParams.clientBranch?.value,
      establishmentBranchId: filterParams.establishmentCompany?.value,
      fiscalDocumentStatus: filterParams.status?.value,
      fiscalDocumentRequirement: 'per_transaction',
    },
    mapper: Mapper.mapWithTransformer({ transformer: BillingEventsFiscalDocumentsTransformer.tableTransformer() })
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <ListHeader
          Icon={<CoinsIcon className="w-10 h-10" strokeWidth={1} />}
          title="Recolha de notas por transações"
        />
      </div>

      <div className="h-[calc((100vh-88px-2rem-1.5rem))] flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            LeftIcon={<Search />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
            }}
            value={filterParams.search}
          />

          <div className="flex items-center justify-start gap-2">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))} />

            <ListFilters
              filters={[
                {
                  className: '',
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: 'Todas', value: '' }, ...billingEventsFiscalDocumentsStatusOptions],
                  placeholder: 'Status'
                },
                {
                  className: '',
                  onChange: (establishmentCompany) => setFilterParams((prev) => ({ ...prev, establishmentCompany })),
                  onInputChange: setSearchEstablishmentCompany,
                  value: filterParams.establishmentCompany,
                  options: [{ label: 'Todas', value: '' }, ...establishmentCompanyOptions as Option[]],
                  placeholder: 'Estabelecimento'
                },
                {
                  className: '',
                  onChange: (clientBranch) => setFilterParams((prev) => ({ ...prev, clientBranch })),
                  onInputChange: setSearchClientBranch,
                  value: filterParams.clientBranch || '',
                  options: [{ label: 'Todas', value: '' }, ...clientBranchesOptions as Option[]],
                  placeholder: 'Filial'
                },
              ]}
            />
          </div>
        </div>

        <Table
          columns={billingEventsFiscalDocumentsDefaultColumns}
          isLoadingData={billingEventsIsFetching}
          page={Number(filterParams.page) || 1}
          rows={billingEvents}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          rowEmptyValue="--"
        />
      </div>
    </div>
  );
};

export default BillingEventsWithFiscalDocumentRequirementList;
